import { useAuth0 } from '@auth0/auth0-react';
import { Container, Theme, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import mixpanel from 'mixpanel-browser';
import React, { useEffect, useMemo, useState } from 'react';
import { API } from '../../../api';
import { ApiError, isApiError } from '../../../api/api';
import { useAlert, useConnectorDeals, useUser } from '../../../contexts';
import { ConnectorDeal, ConnectorDealStatus, ConnectorReferralOption, Role } from '../../../types/types';
import ConnectorReferralModal from '../ConnectorReferralModal';
import ConnectorDealHolder from './ConnectorDealHolder';


const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    paddingLeft: '0px',
    paddingRight: '0px'
  }
}));

interface ConnectorDealsProps {
  renderRightPanelContent: (deal: ConnectorDeal) => React.ReactNode;
  deals: ConnectorDeal[];
  totalCount: number;
  status: ConnectorDealStatus;
}

export type ConnectorDealsCommonProps = Omit<ConnectorDealsProps, 'renderRightPanelContent' | 'status'>;

export default function ConnectorDeals({ renderRightPanelContent, deals, totalCount, status }: ConnectorDealsProps) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { postNewAlert } = useAlert();
  const { getAccessToken, userInfo } = useUser();
  const { currentPage, pageSize, fetchDeals, updateCurrentPage, handleStatusChange } = useConnectorDeals();

  const { sendDealReferrals } = API;

  const [pendingReferral, setPendingReferral] = useState<ConnectorDeal|null>(null);
  const [referralOptions, setReferralOptions] = useState<ConnectorReferralOption[]>([]);

  const handleReferralClick = (deal: ConnectorDeal) => {
    setPendingReferral(deal);
  };

  const handleSendReferrals = async (values: any) => {
    if (!pendingReferral) {
      return;
    }

    const investors = values.selectedInvestors.map((v:ConnectorReferralOption) => (
      { email: v.email, name: v.contactName, id: v.investorProfileId }
    ));
    const { referralMessage, additionalEmails } = values;
    const input = { investors, dealId: pendingReferral.id, message: referralMessage, additionalRecipients: additionalEmails };
    const token = await getAccessToken();

    try {
      const { success, dealStatusUpdated } = await sendDealReferrals(token, input);
      if (status === ConnectorDealStatus.Done || status === ConnectorDealStatus.InProgress) {
        await fetchDeals(status, currentPage);
      } else if (success && dealStatusUpdated) {
        handleStatusChange(pendingReferral.id, ConnectorDealStatus.InProgress);
      }
      setPendingReferral(null);
    } catch (e: unknown) {
      if (isApiError(e)) {
        const { message } = (e as ApiError);
        postNewAlert(message);
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    updateCurrentPage(page, status);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // do not use auto scroll-restoration for this component; sometimes causes jumping to bottom of page
  // when current page changes, after the above effect has scrolled to top
  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    history.scrollRestoration = 'manual';
    return (() => {
      // eslint-disable-next-line no-restricted-globals
      history.scrollRestoration = 'auto';
    });
  }, []);

  useEffect(() => {
    const getConnectorReferralOptions = async (entrepreneurProfileId: number) => {
      try {
        const options = await API.getConnectorReferralOptions(await getAccessToken(), entrepreneurProfileId);
        setReferralOptions(options);
      } catch (e: unknown) {
        console.error(e);
        postNewAlert('An error occurred fetching referral options.');
      }
    };
    if (userInfo && userInfo.role === Role.Connector && pendingReferral) {
      getConnectorReferralOptions(pendingReferral.entrepreneurProfileId);
    }
  }, [getAccessToken, postNewAlert, userInfo, pendingReferral]);

  const { user } = useAuth0();

  useEffect(() => {
    if (userInfo?.role === 'connector') {
      mixpanel.identify(user.sub);
      mixpanel.people.set_once({
        UserType: 'Connector',
        ConnectorID: userInfo?.profileId
      });
      mixpanel.people.set({
        $email: userInfo?.email,
        $name: userInfo?.name
      });
    }
  }, [userInfo?.email, userInfo?.name, user.id, user, userInfo?.profileId, userInfo?.role]);


  const dealsList = useMemo(() => (deals.map((deal: ConnectorDeal) => (
    <ConnectorDealHolder deal={deal} renderRightPanelContent={renderRightPanelContent} handleReferralClick={handleReferralClick} key={deal.id} />
  ))), [deals, renderRightPanelContent]);

  return (
    <Container maxWidth={false} className={classes.root}>
      <ConnectorReferralModal
        open={!(!pendingReferral)}
        referralOptions={referralOptions}
        onClose={() => { setPendingReferral(null); }}
        handleSubmit={handleSendReferrals}
      />
      {dealsList}
      {totalCount > 0 && <Pagination count={Math.ceil(totalCount / pageSize)} page={currentPage} color='primary' onChange={handlePageChange} />}
    </Container>
  );
}
